import type { Preferences, PreferencesPort } from '@/ports/preferences';
import { DEFAULT_PREFERENCES } from '@/ports/preferences';
import {
  CALCULATION_METHODS,
  DEFAULT_TIMES_SETTINGS,
  MADHABS,
  isCoordinates,
} from '@bonyaan/prayer-times';
import { LOCALES } from '@/i18n/types';
import { ACCENTS, FUN_LEVELS, SCHEME_CHOICES, SKINS } from '@/theme/types';
import { readJson, writeJson } from './kv';

const KEY = 'preferences';

function oneOf<T extends string>(allowed: readonly T[], value: unknown, fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

/** Stored preferences are just as untrusted as anything else off a device. */
function coerce(value: unknown): Preferences {
  const v = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>;
  const override = oneOf([...SKINS, 'none'] as const, v.skinOverride ?? 'none', 'none');
  return {
    locale: oneOf(LOCALES, v.locale, DEFAULT_PREFERENCES.locale),
    scheme: oneOf(SCHEME_CHOICES, v.scheme, DEFAULT_PREFERENCES.scheme),
    accent: oneOf(ACCENTS, v.accent, DEFAULT_PREFERENCES.accent),
    fun: oneOf(FUN_LEVELS, v.fun, DEFAULT_PREFERENCES.fun),
    skinOverride: override === 'none' ? null : override,
    times: coerceTimes(v.times),
  };
}

function coerceTimes(value: unknown) {
  const v = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>;
  return {
    // Coordinates come back from storage as untrusted as anything else: a bad
    // pair would send the calculation somewhere impossible rather than fail.
    coordinates: isCoordinates(v.coordinates) ? v.coordinates : null,
    method: oneOf(CALCULATION_METHODS, v.method, DEFAULT_TIMES_SETTINGS.method),
    madhab: oneOf(MADHABS, v.madhab, DEFAULT_TIMES_SETTINGS.madhab),
  };
}

export class LocalPreferences implements PreferencesPort {
  async get(): Promise<Preferences> {
    return coerce(readJson<unknown>(KEY));
  }

  async set(patch: Partial<Preferences>): Promise<Preferences> {
    const next = coerce({ ...(await this.get()), ...patch });
    writeJson(KEY, next);
    return next;
  }
}
